import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Timeline } from 'flowbite-react';
import { RootState, useAppDispatch, useAppSelector } from '../../redux';
import { loadLogs } from '../../redux';
import Log from '../../models/Log';
import Project from '../../models/Project';
import { Icon } from '../Icon';
import { UserAvatar } from '../UserAvatar';
import { WidgetSkeleton } from '../WidgetSkeleton';
import ReactTimeAgo from 'react-time-ago';
import moment from 'moment';
import { formatMomentDate } from '../../locales';
import {
    groupActivityBursts,
    groupConsecutiveActivityLogs,
    GroupedActivityLog,
    isActivityBurst,
} from '../../utils/groupActivityLogs';
import {
    ActivityCategory,
    activityEntrySignal,
    groupByActivityDay,
    MineActivityCategory,
    matchesMineActivityCategory,
} from '../../utils/activityDigest';
import { ActivityBurstRow } from '../ActivityBurstRow';

interface LastActivityProps {
    title?: string;
    limit?: number;
    project?: string;
    /** Restrict to a single author (e.g. current user for "My activity"). */
    author?: string;
    /** Restrict to projects of a client (API filter). */
    client?: string;
    /** Inclusive lower bound on createdAt (ISO). */
    from?: string;
    /** Inclusive upper bound on createdAt (ISO). */
    to?: string;
    /** Exact log type filter forwarded to the API. */
    type?: string;
    /** Chip set: team feed (dashboard) vs personal action kinds. */
    categoryMode?: 'team' | 'mine';
    withHeader?: boolean;
    /** Optional link shown in the widget header (e.g. "View my activity"). */
    headerLinkTo?: string;
    headerLinkLabel?: string;
}

const TEAM_CATEGORY_FILTERS: ActivityCategory[] = ['all', 'team', 'email', 'ci', 'sentry', 'quotes'];
const MINE_CATEGORY_FILTERS: MineActivityCategory[] = ['all', 'comments', 'status', 'patches', 'quotes'];

function ActivityTimestamp(props: { createdAt: moment.Moment; language: string }) {
    const { createdAt, language } = props;
    const { t } = useTranslation();
    const now = moment();

    if (moment.duration(now.diff(createdAt)).asHours() < 48) {
        return (
            <ReactTimeAgo
                locale={language}
                date={createdAt.toDate()}
            />
        );
    }

    return <>{`${t('logs.on')} ${formatMomentDate(createdAt, 'll', language)}`}</>;
}

function DashboardActivityBurstRow(props: {
    entry: GroupedActivityLog;
    showProjectBadge: boolean;
    language: string;
}) {
    const { entry, showProjectBadge, language } = props;
    const { t, i18n } = useTranslation();
    const log = entry.log;
    const summaryHtml = t('logs.outside.worked_on_change', {
        firstname: log.author?.firstname,
        card: log.change?.localizedTitle(i18n.language),
        link: Project.path(log.project, 'change', log.change?._id),
    });

    return (
        <ActivityBurstRow
            entry={entry}
            summaryHtml={summaryHtml}
            showProjectBadge={showProjectBadge}
            renderTimestamp={(createdAt) => (
                <ActivityTimestamp createdAt={createdAt} language={language} />
            )}
        />
    );
}

function ActivitySingleRow(props: {
    entry: GroupedActivityLog;
    showProjectBadge: boolean;
    language: string;
    isLow: boolean;
}) {
    const { entry, showProjectBadge, language, isLow } = props;
    const { t } = useTranslation();

    return (
        <>
            <div className={`flex items-start justify-between gap-2 ${isLow ? 'text-xs text-gray-500 dark:text-gray-400' : 'text-sm'}`}>
                <div>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: entry.log.describe(/*fromOutside*/ true),
                        }}
                    />
                    {entry.count > 1 && (
                        <span className="text-gray-500 dark:text-gray-400">
                            {' '}
                            {t('logs.repeat_count', { count: entry.count })}
                        </span>
                    )}
                </div>
                {showProjectBadge && entry.log.project?.name && (
                    <span
                        className="shrink-0 rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300"
                        data-testid="activity-project-badge"
                    >
                        {entry.log.project.name}
                    </span>
                )}
            </div>
            <div className="text-xs italic text-gray-500 dark:text-gray-400">
                <ActivityTimestamp createdAt={entry.createdAt} language={language} />
            </div>
        </>
    );
}

function LastActivity(props: LastActivityProps) {
    const {
        title,
        withHeader,
        limit,
        project,
        author,
        client,
        from,
        to,
        type,
        categoryMode = 'team',
        headerLinkTo,
        headerLinkLabel,
    } = props;

    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();
    const { logs, loading } = useAppSelector((state: RootState) => state.logs);
    const [teamCategory, setTeamCategory] = useState<ActivityCategory>('all');
    const [mineCategory, setMineCategory] = useState<MineActivityCategory>('all');
    const showProjectBadge = !project;
    const isMine = categoryMode === 'mine';
    // Show skeleton on initial load and on every chip refetch (not only when logs are empty).
    const isLoading = loading === 'pending' || loading === 'idle';

    useEffect(() => {
        const filters: AnyObj = { limit: limit || 15 };
        if (project)
            filters.project = project;
        if (author)
            filters.author = author;
        if (client)
            filters.client = client;
        if (from)
            filters.from = from;
        if (to)
            filters.to = to;
        if (type)
            filters.type = type;
        // Team chips refetch server-side so `$limit` applies after category filter.
        if (!isMine && teamCategory !== 'all')
            filters.category = teamCategory;
        dispatch(loadLogs(filters));
    }, [dispatch, limit, project, author, client, from, to, type, isMine, teamCategory]);

    const sorted = useMemo(() => {
        return [...(logs || [])].sort((a: Log, b: Log) => {
            return a.createdAt?.isAfter(b.createdAt) ? -1 : 1;
        });
    }, [logs]);

    const sections = useMemo(() => {
        const identical = groupConsecutiveActivityLogs(sorted);
        const grouped = groupActivityBursts(identical);
        // Team mode: category already applied by API. Mine mode still filters locally.
        const filtered = isMine
            ? grouped.filter((entry) => matchesMineActivityCategory(entry.log, mineCategory))
            : grouped;
        return groupByActivityDay(filtered);
    }, [sorted, isMine, mineCategory]);

    const emptyState = (
        <div className="flex items-center justify-start py-2 text-sm text-gray-500 dark:text-gray-400">
            <Icon type="thumbs-up" size={6} className="mr-1.5 inline" />
            {t('dashboard.no_activity_for_now')}
        </div>
    );

    const filterChips = (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="activity-filters">
            {(isMine ? MINE_CATEGORY_FILTERS : TEAM_CATEGORY_FILTERS).map((value) => {
                const active = isMine ? mineCategory === value : teamCategory === value;
                return (
                    <button
                        key={value}
                        type="button"
                        onClick={() => {
                            if (isMine)
                                setMineCategory(value as MineActivityCategory);
                            else
                                setTeamCategory(value as ActivityCategory);
                        }}
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium border transition-colors ${
                            active
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-400'
                        }`}
                    >
                        {t(`logs.filter_${value}`)}
                    </button>
                );
            })}
        </div>
    );

    // Keep header + chips visible while refetching so the active chip stays obvious.
    const listSkeleton = (
        <WidgetSkeleton
            rows={4}
            withHeader={false}
            compact
            className="border-0 shadow-none bg-transparent"
        />
    );

    const dayLabel = (section: ReturnType<typeof groupByActivityDay>[number]) => {
        if (section.labelKey === 'day_today')
            return t('logs.day_today');
        if (section.labelKey === 'day_yesterday')
            return t('logs.day_yesterday');
        return formatMomentDate(section.date, 'll');
    };

    const timeline = sections.length > 0 ? (
        <div className="space-y-4" data-testid="activity-digest">
            {sections.map((section) => (
                <div key={section.key} data-testid={`activity-day-${section.key}`}>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {dayLabel(section)}
                    </h4>
                    <Timeline className="ml-4 my-0 p-0">
                        {section.entries.map((entry, i: number) => {
                            const signal = activityEntrySignal(entry);
                            const isLow = signal === 'low';
                            const isHigh = signal === 'high';
                            const burst = isActivityBurst(entry);

                            return (
                                <Timeline.Item
                                    key={entry.log._id || `${section.key}-${i}`}
                                    className="mb-0"
                                    theme={{ root: { vertical: 'mb-3 ml-6' } }}
                                    data-testid={`activity-row-${signal}`}
                                >
                                    <Timeline.Point
                                        icon={() => (
                                            <UserAvatar
                                                user={entry.log.author}
                                                size={isLow ? 6 : 7}
                                                hideInitials
                                            />
                                        )}
                                    />
                                    <Timeline.Content>
                                        <Timeline.Body>
                                            <div
                                                className={`pl-2 ${
                                                    isHigh ? 'border-l-2 border-gray-800' : ''
                                                } ${isLow ? 'opacity-60' : ''}`}
                                            >
                                                {burst ? (
                                                    <DashboardActivityBurstRow
                                                        entry={entry}
                                                        showProjectBadge={showProjectBadge}
                                                        language={i18n.language}
                                                    />
                                                ) : (
                                                    <ActivitySingleRow
                                                        entry={entry}
                                                        showProjectBadge={showProjectBadge}
                                                        language={i18n.language}
                                                        isLow={isLow}
                                                    />
                                                )}
                                            </div>
                                        </Timeline.Body>
                                    </Timeline.Content>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                </div>
            ))}
        </div>
    ) : emptyState;

    const body = isLoading ? listSkeleton : timeline;

    if (!withHeader) {
        return (
            <Card>
                {title && (
                    <h2 className="flex items-center text-2xl font-bold leading-7 text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
                        <Icon type="activity" size={7} className="mr-1.5" />
                        {title}
                    </h2>
                )}
                {filterChips}
                <div className="py-3">
                    {body}
                </div>
            </Card>
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <div className="flex items-end justify-between gap-3">
                    <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                        <Icon type="activity" size={6} className="mr-1.5" />
                        {title || t('dashboard.last_activity')}
                    </h3>
                    {headerLinkTo && headerLinkLabel && (
                        <Link
                            to={headerLinkTo}
                            className="text-xs text-indigo-600 hover:underline"
                            data-testid="activity-header-link"
                        >
                            {headerLinkLabel}
                            <Icon type="right" className="inline" />
                        </Link>
                    )}
                </div>
                {filterChips}
            </div>
            <div className="px-4 py-3">
                {body}
            </div>
        </div>
    );
}

export default LastActivity;
